import { useCallback, useEffect, useState } from 'react';
import { MoonStar, Sun } from 'lucide-react';
import { Button } from '@smallbiz/ui-primitives';

type Theme = 'light' | 'dark';

const THEME_STORAGE_KEY = 'sbi-theme';

function sendThemeToggleEvent(theme: Theme) {
  // DISABLED: Analytics handled by Cloudflare Zaraz and PostHog
  // Prevents 405 errors from non-existent /api/events/theme-toggle endpoint
  // Theme changes are automatically tracked by PostHog session replay
  void theme; // Satisfy unused parameter
  return;
}

function getStoredTheme(): Theme | null {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
    if (stored === 'light' || stored === 'dark') {
      return stored;
    }
  } catch {
    // localStorage unavailable (private mode, etc.). Ignore and fall back to system preference.
  }

  return null;
}

function getPreferredTheme(): Theme {
  if (typeof window === 'undefined') {
    return 'light';
  }

  const stored = getStoredTheme();
  if (stored) {
    return stored;
  }

  const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
  return mediaQuery.matches ? 'dark' : 'light';
}

function applyTheme(theme: Theme) {
  if (typeof document === 'undefined') {
    return;
  }

  const root = document.documentElement;
  if (theme === 'dark') {
    root.classList.add('dark');
  } else {
    root.classList.remove('dark');
  }
}

export function ThemeSwitcher() {
  const [theme, setTheme] = useState<Theme>(() => getPreferredTheme());
  const [hasStoredPreference, setHasStoredPreference] = useState(() => getStoredTheme() !== null);

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  useEffect(() => {
    if (typeof window === 'undefined' || hasStoredPreference) {
      return;
    }

    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (event: MediaQueryListEvent) => {
      setTheme(event.matches ? 'dark' : 'light');
    };

    media.addEventListener('change', handleChange);
    return () => media.removeEventListener('change', handleChange);
  }, [hasStoredPreference]);

  const handleToggle = useCallback(() => {
    const nextTheme: Theme = theme === 'dark' ? 'light' : 'dark';
    setTheme(nextTheme);

    if (typeof window !== 'undefined') {
      try {
        window.localStorage.setItem(THEME_STORAGE_KEY, nextTheme);
        setHasStoredPreference(true);
      } catch {
        // Ignore storage write failures so the toggle still works visually.
      }
    }

    sendThemeToggleEvent(nextTheme);
  }, [theme]);

  const isDark = theme === 'dark';
  const Icon = isDark ? Sun : MoonStar;
  const label = isDark ? 'Switch to light theme' : 'Switch to dark theme';

  return (
    <Button
      variant="ghost"
      size="sm"
      type="button"
      onClick={handleToggle}
      aria-pressed={isDark}
      aria-label={label}
      className="flex items-center justify-center rounded-full border border-border bg-card p-2 text-foreground hover:text-primary hover:bg-primary/5 hover:border-primary/40 sbi-shadow-xs hover:sbi-shadow-sm active:sbi-shadow-inset-soft transition-all duration-200 w-10 h-10"
    >
      <Icon className="size-5 shrink-0" aria-hidden="true" />
      <span className="sr-only">{label}</span>
    </Button>
  );
}
